import { useCallback, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'

import { ApiUrlPath, IQuestion } from 'cat-shared/utils'
import { getRandNum } from 'cat-shared/helpers'
import { useGetQuestionCategories } from 'client/api'
import { displaySomethingWentWrongError } from 'client/services'
import { usePage } from 'client/hooks'

import {
  AppDivider,
  AppParagraph,
  AppSegment,
  AppButton,
  AppTable,
  AppLoader,
  AppFlexContainer,
  AppFlexItem,
} from 'client/ui-building-blocks'

import {
  TGetProposedQuestionsData,
  useDeleteProposedQuestions,
  useGetProposedQuestions,
  useGetQuestionProposal,
} from './api'

import { DataManagerForm } from './components/DataManagerForm'
import { AcceptButton } from './components/AcceptButton'

const TABLE_COLUMNS = [
  'type',
  'category',
  'text',
  'options',
  'correctOptions',
  'explanation',
  '',
]

export const DataManager = () => {
  const queryClient = useQueryClient()

  const { navigateTo } = usePage()

  const { data: questionCategories } = useGetQuestionCategories({})

  const formContext = useForm({
    defaultValues: {
      category:
        questionCategories.en[getRandNum(0, questionCategories.en.length - 1)].key,
      subject: '',
    },
  })

  const {
    isLoading: isLoadingProposedQuestions,
    isFetching: isFetchingProposedQuestions,
    refetch: fetchProposedQuestions,
    data: proposedQuestions,
  } = useGetProposedQuestions()

  const { isFetching: isFetchingQuestionProposal, refetch: fetchQuestionProposal } =
    useGetQuestionProposal(() => formContext.getValues())

  const { mutateAsync: deleteProposedQuestions, isLoading: isDeletingProposedQuestions } =
    useDeleteProposedQuestions()

  useEffect(() => {
    fetchProposedQuestions()
  }, [fetchProposedQuestions])

  const onBackToAppBtnClick = () => {
    navigateTo({ path: '/' })
  }

  const onGenerateQuestionBtnClick = async () => {
    const res = await fetchQuestionProposal()

    if (!res.isError) {
      queryClient.setQueryData(
        [ApiUrlPath.GetProposedQuestions],
        ({ count, data }: TGetProposedQuestionsData) => {
          return {
            count: count + 1,
            data: [...data, res.data],
          }
        }
      )
    } else {
      displaySomethingWentWrongError()
    }
  }

  const onRejectAllBtnClick = async () => {
    await deleteProposedQuestions({
      questionIds: proposedQuestions.data.map(q => q._id.toString()),
    })

    queryClient.setQueryData([ApiUrlPath.GetProposedQuestions], () => {
      return {
        count: 0,
        data: [],
      }
    })
  }

  const onCheckBtnClick = (text: string) => () => {
    window.open(`https://google.com/search?q=${text}`, '_blank')
  }

  const mapValue = useCallback(
    (rowData: IQuestion, colName: string) => {
      const cellValue = rowData[colName]

      if (colName === TABLE_COLUMNS[1]) {
        return (
          <>
            {
              questionCategories.en.find(
                item => item.key === formContext.getValues('category')
              ).text
            }
          </>
        )
      } else if (colName === TABLE_COLUMNS[3]) {
        return (
          <ul style={{ paddingLeft: '15px' }}>
            {(cellValue as string[]).map((option, i) => (
              <li key={i}>{option}</li>
            ))}
          </ul>
        )
      } else if (colName === TABLE_COLUMNS[4]) {
        return (
          <ul style={{ paddingLeft: '15px' }}>
            {(cellValue as number[]).map((correctOptionIndex, i) => (
              <li key={i}>{rowData.options[correctOptionIndex]}</li>
            ))}
          </ul>
        )
      } else if (colName === TABLE_COLUMNS[6]) {
        return (
          <AppFlexContainer justifyContent="center" rowGap="7px" columnGap="7px">
            <AppFlexItem>
              <AppButton
                text="Check"
                variant="outlined"
                onClick={onCheckBtnClick(rowData.text)}
              />
            </AppFlexItem>
            <AppFlexItem>
              <AcceptButton question={rowData} disabled={isFetchingQuestionProposal} />
            </AppFlexItem>
          </AppFlexContainer>
        )
      } else {
        return <>{cellValue}</>
      }
    },
    [questionCategories, isFetchingQuestionProposal]
  )

  const Footer = (
    <AppButton
      variant="error"
      text="Reject all"
      loading={isDeletingProposedQuestions}
      disabled={isFetchingQuestionProposal}
      onClick={onRejectAllBtnClick}
    />
  )

  return (
    <AppSegment
      headerProps={{
        text: 'Data Manager',
        iconProps: {
          name: 'DATABASE',
        },
        size: 'large',
        marginBottom: 'small',
      }}
      centered
    >
      <AppParagraph
        text="A place for managing all application data, especially for handling questions creation process."
        marginBottom="xsmall"
      />
      <AppButton
        text="Back to app"
        iconName="ANGLE_DOUBLE_LEFT"
        onClick={onBackToAppBtnClick}
      />
      <AppDivider />
      <AppSegment
        headerProps={{
          text: `Proposed questions (${proposedQuestions?.count || 0})`,
          marginBottom: 'small',
        }}
      >
        <FormProvider {...formContext}>
          <DataManagerForm
            isFetchingQuestionProposal={isFetchingQuestionProposal}
            onGenerateQuestionBtnClick={onGenerateQuestionBtnClick}
          />
          <AppLoader active={isLoadingProposedQuestions || isFetchingProposedQuestions}>
            <AppTable
              columns={TABLE_COLUMNS}
              rows={proposedQuestions?.data || []}
              mapValue={mapValue}
              Footer={proposedQuestions?.data.length > 0 ? Footer : null}
              showRowNo
              hideHeader
            />
          </AppLoader>
        </FormProvider>
      </AppSegment>
    </AppSegment>
  )
}
